// src/components/Header.js
import React, { useRef, useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import logo from "../assets/last_logo.png";
import { useAuth } from "../context/AuthContext";
import { FiLogOut } from "react-icons/fi";
import {
  HomeIcon,
  UsersIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import ConfirmModal from "./ConfirmModal";
import HeaderSubscriptionStatus from "./HeaderSubscriptionStatus";

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const headerRef = useRef(null);
  const [showConfirmLogout, setShowConfirmLogout] = useState(false);
  const tempUser = user;
  const isLoggedIn = !!tempUser;
  const userRole = (tempUser?.role || "").toLowerCase();
  const canViewAdmin = userRole === "admin";
  const canViewPeople = userRole === "organizer";
  const canViewStatistics = userRole === "admin" || userRole === "organizer";

  const handleDashboardClick = () => {
    if (tempUser) {
      if (userRole === "worker") {
        navigate("/worker-dashboard");
      } else {
        navigate("/dashboard");
      }
    } else {
      navigate("/");
    }
  };

  const handleLogout = () => {
    setShowConfirmLogout(false);
    logout();
    navigate("/login");
  };

  const isActive = (path) => {
    if (path === "/dashboard" || path === "/worker-dashboard") {
      return (
        location.pathname === "/dashboard" ||
        location.pathname === "/worker-dashboard"
      );
    }
    return location.pathname.startsWith(path);
  };

  const isInviteLinkPage = location.pathname.startsWith("/invite/");
  const dashboardLink =
    userRole === "worker" ? "/worker-dashboard" : "/dashboard";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        // no-op
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <header
        ref={headerRef}
        className="bg-gray-800 text-white fixed top-0 left-0 w-full z-50 shadow-lg overflow-visible"
      >
        <div className="container mx-auto max-w-5xl px-4 flex items-center justify-between h-14">
          <div className="flex items-center h-full">
            <div
              className="flex items-center cursor-pointer h-full pl-0 sm:pl-2"
              onClick={handleDashboardClick}
            >
              <img src={logo} alt="Juza.pro Logo" className="h-7 w-auto" />
            </div>
          </div>

          {/* --- Мобильное меню --- */}
          {!isInviteLinkPage && isLoggedIn && (
            <div className="md:hidden flex items-center space-x-4">
              <Link
                to={dashboardLink}
                className={`flex items-center justify-center w-8 h-8 rounded-md transition duration-200 ${
                  isActive("/dashboard") ? "text-indigo-400" : "hover:bg-gray-700"
                }`}
                title="Dashboard"
              >
                <HomeIcon className="h-5 w-5" />
              </Link>
              {canViewAdmin && (
                <Link
                  to="/c7d01"
                  className={`flex items-center justify-center w-8 h-8 rounded-md transition duration-200 ${
                    isActive("/admin") ? "text-indigo-400" : "hover:bg-gray-700"
                  }`}
                  title="Admin Dashboard"
                >
                  <ShieldCheckIcon className="h-5 w-5" />
                </Link>
              )}
              {/* Только для organizer */}
              {canViewPeople && (
                <Link
                  to="/people"
                  className={`flex items-center justify-center w-8 h-8 rounded-md transition duration-200 ${
                    isActive("/people") ? "text-indigo-400" : "hover:bg-gray-700"
                  }`}
                  title="People"
                >
                  <UsersIcon className="h-5 w-5" />
                </Link>
              )}
              {/* Statistics: admin and organizer */}
              {canViewStatistics && (
                <Link
                  to="/statistics"
                  className={`flex items-center justify-center w-8 h-8 rounded-md transition duration-200 ${
                    isActive("/statistics")
                      ? "text-indigo-400"
                      : "hover:bg-gray-700"
                  }`}
                  title="Statistics"
                >
                  <ChartBarIcon className="h-5 w-5" />
                </Link>
              )}
              <Link
                to="/settings"
                className={`flex items-center justify-center w-8 h-8 rounded-md transition duration-200 ${
                  isActive("/settings")
                    ? "text-indigo-400"
                    : "hover:bg-gray-700"
                }`}
                title="Settings"
              >
                <Cog6ToothIcon className="h-5 w-5" />
              </Link>
              <button
                onClick={() => setShowConfirmLogout(true)}
                className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-700 transition duration-200"
                title="Logout"
              >
                <FiLogOut className="h-5 w-5" />
              </button>
              <HeaderSubscriptionStatus />
            </div>
          )}

          {/* --- Десктопное меню --- */}
          {!isInviteLinkPage && (
            <nav className="hidden md:flex space-x-2 items-center">
              {isLoggedIn && (
                <>
                  <Link
                    to={dashboardLink}
                    className={`flex items-center px-3 py-2 rounded transition duration-200 ${
                      isActive("/dashboard")
                        ? "text-indigo-400 font-medium"
                        : "hover:bg-gray-700"
                    }`}
                  >
                    <HomeIcon className="h-5 w-5 mr-1" />
                    Dashboard
                  </Link>
                  {canViewAdmin && (
                    <Link
                      to="/c7d01"
                      className={`flex items-center px-3 py-2 rounded transition duration-200 ${
                        isActive("/admin")
                          ? "text-indigo-400 font-medium"
                          : "hover:bg-gray-700"
                      }`}
                    >
                      <ShieldCheckIcon className="h-5 w-5 mr-1" />
                      Admin
                    </Link>
                  )}
                  {/* Только для organizer */}
                  {canViewPeople && (
                    <Link
                      to="/people"
                      className={`flex items-center px-3 py-2 rounded transition duration-200 ${
                        isActive("/people")
                          ? "text-indigo-400 font-medium"
                          : "hover:bg-gray-700"
                      }`}
                    >
                      <UsersIcon className="h-5 w-5 mr-1" />
                      People
                    </Link>
                  )}
                  {/* Statistics: admin and organizer */}
                  {canViewStatistics && (
                    <Link
                      to="/statistics"
                      className={`flex items-center px-3 py-2 rounded transition duration-200 ${
                        isActive("/statistics")
                          ? "text-indigo-400 font-medium"
                          : "hover:bg-gray-700"
                      }`}
                    >
                      <ChartBarIcon className="h-5 w-5 mr-1" />
                      Statistics
                    </Link>
                  )}
                  <Link
                    to="/settings"
                    className={`flex items-center px-3 py-2 rounded transition duration-200 ${
                      isActive("/settings")
                        ? "text-indigo-400 font-medium"
                        : "hover:bg-gray-700"
                    }`}
                  >
                    <Cog6ToothIcon className="h-5 w-5 mr-1" />
                    Settings
                  </Link>
                  <button
                    onClick={() => setShowConfirmLogout(true)}
                    className="flex items-center px-3 py-2 hover:bg-gray-700 rounded transition duration-200"
                  >
                    <FiLogOut className="mr-1 h-5 w-5" />
                    Logout
                  </button>
                  <HeaderSubscriptionStatus />
                </>
              )}

              {!isLoggedIn && (
                <>
                  {location.pathname === "/" && (
                    <>
                      <Link
                        to="/login"
                        className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                      >
                        Login
                      </Link>
                      <Link
                        to="/signup"
                        className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 transition duration-200"
                      >
                        Sign Up
                      </Link>
                    </>
                  )}
                  {location.pathname === "/login" && (
                    <Link
                      to="/signup"
                      className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 transition duration-200"
                    >
                      Sign Up
                    </Link>
                  )}
                  {location.pathname === "/signup" && (
                    <Link
                      to="/login"
                      className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                    >
                      Login
                    </Link>
                  )}
                </>
              )}
            </nav>
          )}
        </div>
      </header>
      <ConfirmModal
        isOpen={showConfirmLogout}
        onClose={() => setShowConfirmLogout(false)}
        onConfirm={handleLogout}
        message="Are you sure you want to log out?"
      />
    </>
  );
}

export default Header;
